import {
	useEffect,
	useMemo,
	useState,
	type ChangeEvent,
	type FormEvent,
} from 'react'

import { Field } from '#app/components/form/field.tsx'
import { SelectField } from '#app/components/form/select-field.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { ResponsiveOverlay } from '#app/components/ui/responsive-overlay.tsx'
import { type IntakeChannelDraft } from '#app/routes/mocra+/_form+/adverse-events/components/intake-channels/types.ts'

type Option = {
	readonly value: string
	readonly label: string
}

type Props = {
	open: boolean
	mode: 'add' | 'edit'
	channelLabel: string
	initialValue: IntakeChannelDraft
	ownerTypeOptions: ReadonlyArray<Option>
	systemOptions: ReadonlyArray<Option>
	onSubmit: (draft: IntakeChannelDraft) => void
	onOpenChange: (open: boolean) => void
	onCancel?: () => void
	onClosed?: () => void
	onRemove?: () => void
}

type DraftErrors = Partial<
	Pick<IntakeChannelDraft, 'ownerName' | 'ownerType' | 'system'>
>

const COPY = {
	title: {
		add: 'Assign intake channel ownership',
		edit: 'Assign intake channel ownership',
	},
	description:
		'Assign who operates this intake channel and where complaints are logged after intake.',
	submit: {
		add: 'Save',
		edit: 'Save',
	},
	fields: {
		ownerTypeLabel: 'Who owns or operates this channel?',
		ownerNameLabel:
			'Name the specific retailer, platform, or team supporting it.',
		systemLabel: 'Where do complaints from this channel get logged?',
	},
	warnings: {
		ownerTypeMissing: 'Select the owner or operator for this channel.',
		ownerNameMissing:
			'Add the retailer, platform, or team responsible for the channel.',
		systemMissing:
			'Pick the system of record or fallback workflow where complaints land.',
	},
	cancel: 'Cancel',
} as const

export function IntakeChannelOverlay({
	open,
	mode,
	channelLabel,
	initialValue,
	ownerTypeOptions,
	systemOptions,
	onSubmit,
	onOpenChange,
	onCancel,
	onClosed,
	onRemove,
}: Props) {
	const [draft, setDraft] = useState<IntakeChannelDraft>(initialValue)
	const [errors, setErrors] = useState<DraftErrors>({})

	useEffect(() => {
		if (!open) {
			onClosed?.()
			return
		}
		setDraft(initialValue)
		setErrors({})
	}, [initialValue, onClosed, open])

	const ownerTypeMap = useMemo(() => {
		const map = new Map(
			ownerTypeOptions.map((option) => [option.value, option]),
		)
		return map
	}, [ownerTypeOptions])

	const handleOwnerTypeChange = (value: string) => {
		setDraft((prev) => ({ ...prev, ownerType: value }))
	}

	const handleOwnerNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		setDraft((prev) => ({ ...prev, ownerName: event.target.value }))
	}

	const handleSystemChange = (value: string) => {
		setDraft((prev) => ({ ...prev, system: value }))
	}

	const sanitize = (input: IntakeChannelDraft): IntakeChannelDraft => ({
		channel: input.channel,
		ownerType: input.ownerType,
		ownerName: input.ownerName.trim(),
		system: input.system,
	})

	const validate = (input: IntakeChannelDraft): DraftErrors => {
		const nextErrors: DraftErrors = {}
		if (!ownerTypeMap.has(input.ownerType)) {
			nextErrors.ownerType = COPY.warnings.ownerTypeMissing
		}
		if (input.ownerName.trim().length === 0) {
			nextErrors.ownerName = COPY.warnings.ownerNameMissing
		}
		const systemMatch = systemOptions.some(
			(option) => option.value === input.system,
		)
		if (!systemMatch) {
			nextErrors.system = COPY.warnings.systemMissing
		}
		return nextErrors
	}

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		event.stopPropagation()
		const sanitized = sanitize(draft)
		const nextErrors = validate(sanitized)
		if (Object.keys(nextErrors).length > 0) {
			setErrors(nextErrors)
			return
		}
		onSubmit(sanitized)
		onOpenChange(false)
	}

	const handleCancel = () => {
		onOpenChange(false)
		onCancel?.()
	}

	const titleCopy = channelLabel
	const submitCopy = mode === 'add' ? COPY.submit.add : COPY.submit.edit
	const handleRemove = () => {
		if (!onRemove) return
		onRemove()
		onOpenChange(false)
	}

	return (
		<ResponsiveOverlay
			open={open}
			onOpenChange={onOpenChange}
			title={titleCopy}
			description={<p>{COPY.description}</p>}
			footer={
				<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
					<Button type="button" variant="outline" onClick={handleCancel}>
						{COPY.cancel}
					</Button>
					{mode === 'edit' && onRemove ? (
						<Button type="button" variant="destructive" onClick={handleRemove}>
							Remove
						</Button>
					) : (
						<span />
					)}
					<Button type="submit" form="intake-channel-overlay-form">
						{submitCopy}
					</Button>
				</div>
			}
		>
			<form id="intake-channel-overlay-form" onSubmit={handleSubmit}>
				<SelectField
					labelProps={{
						children: COPY.fields.ownerTypeLabel,
					}}
					selectProps={{
						name: 'ownerType',
						value: draft.ownerType,
						onChange: (event: ChangeEvent<HTMLSelectElement>) =>
							handleOwnerTypeChange(event.target.value),
					}}
					options={ownerTypeOptions}
					errors={errors.ownerType ? [errors.ownerType] : undefined}
				/>
				<Field
					labelProps={{
						children: COPY.fields.ownerNameLabel,
					}}
					inputProps={{
						name: 'ownerName',
						value: draft.ownerName,
						onChange: handleOwnerNameChange,
						type: 'text',
					}}
					errors={errors.ownerName ? [errors.ownerName] : undefined}
				/>
				<SelectField
					labelProps={{
						children: COPY.fields.systemLabel,
					}}
					selectProps={{
						name: 'system',
						value: draft.system,
						onChange: (event: ChangeEvent<HTMLSelectElement>) =>
							handleSystemChange(event.target.value),
						onValueChange: handleSystemChange,
					}}
					options={systemOptions}
					errors={errors.system ? [errors.system] : undefined}
				/>
			</form>
		</ResponsiveOverlay>
	)
}
